import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { exportReportPdfServer } from '../export/exportReportPdfServer';
import '../assets/reportFonts.scss';
import BottomDrawer from '../../../common/_custom/BottomDrawer/BottomDrawer';
import { useAppSelector } from '../../../app/hooks';
import { saveDocument, listDocuments } from '../../../store/investor/investorApiHelper';
import { AICA_REPORT_ID } from '../../Reports/constants';
import { useAicaReportV2Logic } from '../useAicaReportV2Logic';
import { ReportPreview } from './ReportPreview';
import { ModuleRail } from './ModuleRail';
import { resolveExistingAicaReportEntry } from './reportLineage';
import styles from './AicaReportV2Drawer.module.scss';

export type AicaReportV2DrawerProps = {
    assesseeOrgId?: string;
    orgName?: string;
    onClose: () => void;
    /** Fired after the downloaded report is persisted to the reports list, so the
     *  caller (Reports page) can refetch and show the new row. */
    onReportSaved?: () => void;
};

const AicaReportV2Drawer: React.FC<AicaReportV2DrawerProps> = ({ assesseeOrgId, orgName, onClose, onReportSaved }) => {
    const logic = useAicaReportV2Logic(assesseeOrgId);
    const [downloadState, setDownloadState] = useState<'idle' | 'preparing' | 'error'>('idle');
    // The exporter serialises this live preview DOM and renders it through the
    // headless-Chromium Lambda — so the on-screen preview IS the export source.
    const previewRef = useRef<HTMLDivElement>(null);

    // PNO (Partner-Network-Org) logo for the cover. Read the active PNO context
    // out of Redux here and hand the URL down to the preview/cover.
    const pnoLogoUrl = useAppSelector((state: any) => state.user?.domainMetadata?.logoUrl as string | undefined);

    // This drawer is mounted from three different call sites (Reports, portfolio
    // header, companies list) that only pass assesseeOrgId/orgName — read the
    // saving user's context straight from Redux (same selectors useReportsLogic
    // uses) rather than threading new props through all three callers.
    const investorOrgId = useAppSelector((state: any) => state.user?.userData?.investorOrgId as string | undefined);
    const createdBy = useAppSelector((state: any) => state.user?.userData?.primaryEmail as string | undefined);

    // Pre-resolve the PNO logo to a data URL with the HTTP cache bypassed. The
    // browser usually has this logo cached from a plain <img> load elsewhere in
    // the app, and that cached copy carries no CORS headers — so the export's
    // own fetch (inlineImagesToDataUris) is blocked and the logo silently
    // vanishes from the cover. Fetching it once here (cache: 'no-store') and
    // embedding it as a data URL makes the export self-contained. On failure we
    // render the cover without a logo rather than break the export.
    const [pnoLogoDataUrl, setPnoLogoDataUrl] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (!pnoLogoUrl) return undefined;
        let cancelled = false;
        fetch(pnoLogoUrl, { cache: 'no-store', mode: 'cors' })
            .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(`HTTP ${res.status}`))))
            .then(
                (blob) =>
                    new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(blob);
                    }),
            )
            .then((dataUrl) => {
                if (!cancelled) setPnoLogoDataUrl(dataUrl);
            })
            .catch((err) => {
                console.warn('AicaReportV2: could not load PNO logo for the cover page', err);
            });
        return () => {
            cancelled = true;
        };
    }, [pnoLogoUrl]);

    const docProps = useMemo(
        () => ({
            data: logic.data,
            enabledModules: logic.enabledModules,
            pnoLogoUrl: pnoLogoDataUrl,
        }),
        [logic.data, logic.enabledModules, pnoLogoDataUrl],
    );

    const fileName = useMemo(() => {
        const name = (logic.data.identity.orgName || orgName || 'report').replace(/[^a-z0-9]+/gi, '_');
        const date = new Date().toISOString().slice(0, 10);
        return `${name}_AICA_Report_${date}.pdf`;
    }, [logic.data.identity.orgName, orgName]);

    const displayName = logic.data.identity.orgName !== '—' ? logic.data.identity.orgName : orgName ?? 'Company';
    const failedSources = Object.keys(logic.errors);

    // Server-side export is the only path: serialise the live preview and render
    // it via the headless-Chromium Lambda. A failure surfaces on the button
    // rather than silently falling back.
    const disableDownload = logic.enabledModules.length === 0 || logic.status === 'loading' || downloadState === 'preparing';
    const handleDownload = async () => {
        if (disableDownload || !previewRef.current) return;
        setDownloadState('preparing');
        try {
            await exportReportPdfServer({ target: previewRef.current, filename: fileName });
            setDownloadState('idle');
        } catch (err) {
            console.error('[AicaReportV2] PDF export failed:', err);
            setDownloadState('error');
            return;
        }
        // Basic metadata only (no content/S3/docId) so the export shows up as a
        // row in the Reports list — clicking it just reopens this drawer, nothing
        // is fetched. The download already succeeded; a failed list-save must not
        // surface an alarming toast, but a successful one must refetch the list so
        // the new row appears without a manual reload.
        if (!assesseeOrgId) return;
        // Reuse this assessee's existing AICA Report lineage (if any) so a repeat
        // download updates that same row instead of minting a fresh one every
        // time. Best-effort: a failed lookup just falls through to no known
        // lineage, same as before this fix — it must not block the save itself.
        let existingId: string | undefined;
        let existingGenId: string | undefined;
        try {
            const existingDocs = await listDocuments({ assesseeOrgId });
            ({ id: existingId, genId: existingGenId } = resolveExistingAicaReportEntry(existingDocs));
        } catch {
            /* best-effort lineage lookup */
        }
        try {
            const saved = await saveDocument({
                assesseeOrgId,
                ...(existingId ? { id: existingId } : {}),
                ...(existingGenId ? { genId: existingGenId } : {}),
                investorOrgId,
                createdBy,
                docType: AICA_REPORT_ID,
                name: fileName,
                formatType: 'PDF',
                content: null,
            });
            if (saved) onReportSaved?.();
        } catch (err) {
            console.error('[AicaReportV2] Could not save the report entry:', err);
        }
    };
    const downloadLabel =
        downloadState === 'preparing' ? 'Preparing PDF…' : downloadState === 'error' ? 'PDF error — retry' : 'Download PDF';

    return (
        <BottomDrawer
            onClose={onClose}
            height="100vh"
            allowClose
            showHeading={false}
            style={{ maxHeight: '100vh', height: '100vh', borderRadius: 0 }}
            iconStyle={{
                position: 'absolute',
                top: 28,
                right: 24,
                marginTop: 0,
                marginRight: 0,
                height: 15,
                width: 15,
                background: 'transparent',
                border: 'none',
                padding: 0,
                borderRadius: 0,
                boxShadow: 'none',
                zIndex: 10,
            }}
        >
            <div className={styles.Root}>
                <div className={styles.TopBar}>
                    <div className={styles.TitleBlock}>
                        <span className={styles.Title}>{displayName}</span>
                        <span className={styles.Sub}>
                            {logic.enabledModules.length} of {logic.modulesPresent.length} modules selected
                        </span>
                    </div>
                    <div className={styles.RightActions}>
                        <button
                            type="button"
                            className={clsx(styles.Download, { [styles.Disabled]: disableDownload })}
                            disabled={disableDownload}
                            onClick={handleDownload}
                        >
                            {downloadLabel}
                        </button>
                    </div>
                </div>

                <div className={styles.Body}>
                    <ModuleRail
                        items={logic.railItems}
                        searchTerm={logic.searchTerm}
                        setSearchTerm={logic.setSearchTerm}
                        toggleModule={logic.toggleModule}
                        selectAll={logic.selectAll}
                        deselectAll={logic.deselectAll}
                        allEnabled={logic.allEnabled}
                        reorder={logic.reorder}
                        loading={logic.status !== 'ready'}
                    />

                    <section className={styles.PreviewCol}>
                        {logic.status === 'error' ? (
                            <div className={clsx(styles.Banner, styles.Error)}>
                                Every data source failed for <strong>{assesseeOrgId}</strong>.
                            </div>
                        ) : failedSources.length > 0 ? (
                            <div className={styles.Banner}>
                                Loaded with {failedSources.length} source{failedSources.length === 1 ? '' : 's'} missing:{' '}
                                {failedSources.join(', ')}
                            </div>
                        ) : null}

                        <div className={styles.PreviewCount}>
                            {logic.status === 'loading'
                                ? 'Fetching report data…'
                                : `Showing ${logic.enabledModules.length} of ${logic.modulesPresent.length} available modules`}
                        </div>

                        {logic.status === 'loading' ? (
                            <div className={styles.Empty}>Fetching report data…</div>
                        ) : logic.enabledModules.length === 0 ? (
                            <div className={styles.Empty}>No modules selected. Pick at least one from the left to preview & export.</div>
                        ) : (
                            <div ref={previewRef}>
                                <ReportPreview {...docProps} />
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </BottomDrawer>
    );
};

export default AicaReportV2Drawer;
